// Client HTTP de l'API : cookies de session (même origine), jeton CSRF par double soumission,
// erreurs normalisées { erreur: { code, message, details } }.

export class ErreurApi extends Error {
  constructor(
    public readonly statut: number,
    public readonly code: string,
    message: string,
    public readonly details: unknown = null,
  ) {
    super(message);
  }
}

export interface Reponse<T> {
  donnees: T;
  meta: Record<string, unknown>;
}

const ecouteursSession = new Set<() => void>();

/** Notifié quand le serveur répond 401 (session expirée, révoquée ou compte suspendu). */
export function surSessionPerdue(ecouteur: () => void): () => void {
  ecouteursSession.add(ecouteur);
  return () => ecouteursSession.delete(ecouteur);
}

export function lireCookie(nom: string): string | null {
  const ligne = document.cookie.split('; ').find((c) => c.startsWith(`${nom}=`));
  return ligne ? decodeURIComponent(ligne.slice(nom.length + 1)) : null;
}

export interface OptionsRequete {
  methode?: 'GET' | 'POST' | 'PUT' | 'PATCH';
  corps?: unknown;
  formulaire?: FormData;
  signal?: AbortSignal;
}

export async function api<T>(chemin: string, options: OptionsRequete = {}): Promise<Reponse<T>> {
  const methode = options.methode ?? (options.corps !== undefined || options.formulaire ? 'POST' : 'GET');
  const entetes: Record<string, string> = { accept: 'application/json' };
  if (methode !== 'GET') {
    const csrf = lireCookie('ct_csrf');
    if (csrf) entetes['x-csrf-token'] = csrf;
  }
  let corps: BodyInit | undefined;
  if (options.formulaire) corps = options.formulaire;
  else if (options.corps !== undefined) {
    entetes['content-type'] = 'application/json';
    corps = JSON.stringify(options.corps);
  }
  let reponse: Response;
  try {
    reponse = await fetch(chemin, { method: methode, headers: entetes, body: corps, credentials: 'same-origin', signal: options.signal });
  } catch {
    throw new ErreurApi(0, 'reseau', 'Serveur injoignable : vérifiez la connexion réseau');
  }
  const texte = await reponse.text();
  let json: unknown = null;
  try {
    json = texte ? JSON.parse(texte) : null;
  } catch {
    json = null;
  }
  if (!reponse.ok) {
    const e = (json as { erreur?: { code: string; message: string; details: unknown } } | null)?.erreur;
    if (reponse.status === 401 && !chemin.startsWith('/api/v1/auth/connexion')) for (const f of ecouteursSession) f();
    throw new ErreurApi(reponse.status, e?.code ?? 'erreur', e?.message ?? `Erreur ${reponse.status}`, e?.details ?? null);
  }
  return json as Reponse<T>;
}

export function messageErreur(e: unknown): string {
  if (e instanceof ErreurApi) {
    const details = Array.isArray(e.details)
      ? e.details.map((d) => (typeof d === 'string' ? d : (d as { message?: string }).message)).filter(Boolean)
      : [];
    return details.length ? `${e.message} : ${details.join(' ; ')}` : e.message;
  }
  return e instanceof Error ? e.message : 'Erreur inattendue';
}
